// frontend/src/services/annualLeave.service.ts
// Annual Leave Application Service - WITH RETURN JOURNEY & DUTY RESUME TRACKING
// PHOENICIAN TECHNICAL SERVICES LLC

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  query, 
  where, 
  Timestamp,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { Airport } from './airport.service';

// ============================================================================
// TYPES
// ============================================================================

export type FlightBookingType = 'byCompany' | 'byEmployee';

export interface FlightBookingOption {
  value: FlightBookingType;
  label: string;
  description: string;
  icon: string;
}

export const FLIGHT_BOOKING_OPTIONS: FlightBookingOption[] = [
  {
    value: 'byCompany',
    label: 'By Company',
    description: 'Company will arrange and book flight tickets',
    icon: '🏢'
  },
  {
    value: 'byEmployee',
    label: 'By Employee',
    description: 'I will arrange and book my own flight tickets',
    icon: '👤'
  }
];

export interface AirportData {
  iataCode: string;
  icaoCode?: string;
  name: string;
  city: string;
  country: string;
}

// Duty Resume Status
export type DutyResumeStatus = 'pending' | 'resumed' | 'extended' | 'absent' | 'not_returned';

export interface DutyResumeDetails {
  dutyResumeStatus: DutyResumeStatus;
  actualReturnDate?: Date;
  dutyResumedDate?: Date;
  dutyResumedBy?: string;
  dutyResumedByName?: string;
  dutyResumeRemarks?: string;
  // Extension details
  extensionDays?: number;
  extensionReason?: string;
  extensionApprovedBy?: string;
  extensionApprovedAt?: Date;
  newExpectedReturnDate?: Date;
}

export interface AnnualLeaveApplicationData {
  // Employee Info
  employeeId: string;
  employeeName: string;
  employeePin: string;
  department?: string;
  designation?: string;
  
  // Leave Dates
  startDate: Date;
  endDate: Date;
  totalDays: number;
  remarks?: string;
  
  // Project Info
  projectCode?: string;
  projectName?: string;
  
  // OUTBOUND Journey (Going to home country)
  flightBookingType: FlightBookingType;
  departureAirport: AirportData;      // From UAE
  arrivalAirport: AirportData;         // To destination country
  
  // RETURN Journey (Coming back to UAE) - NEW
  expectedReturnDate: Date;
  returnDepartureAirport: AirportData; // From destination country
  returnArrivalAirport: AirportData;   // Back to UAE
  returnRemarks?: string;
  returnFlightBookingType?: FlightBookingType;
  
  // Additional Details
  addressDuringLeave: string;
  signatureFullName: string;
  passportNumber?: string;
  
  // Engineer Info
  appliedByEngineerId?: string;
  appliedByEngineerName?: string;
  engineerRemarks?: string;
}

export interface AnnualLeaveApplicationRecord {
  id: string;
  applicationId: string;
  
  // Employee Info
  employeeId: string;
  employeeName: string;
  employeePin: string;
  department?: string;
  designation?: string;
  
  // Leave Details
  leaveType: 'annual';
  startDate: Date;
  endDate: Date;
  totalDays: number;
  remarks?: string;
  
  // Project Info
  projectCode?: string;
  projectName?: string;
  
  // OUTBOUND Journey
  flightBookingType: FlightBookingType;
  departureAirport: AirportData;
  arrivalAirport: AirportData;
  
  // RETURN Journey - NEW
  expectedReturnDate: Date;
  returnDepartureAirport: AirportData;
  returnArrivalAirport: AirportData;
  returnRemarks?: string;
  returnFlightBookingType?: FlightBookingType;
  
  // Duty Resume Tracking - NEW
  dutyResumeStatus: DutyResumeStatus;
  actualReturnDate?: Date;
  dutyResumedDate?: Date;
  dutyResumedBy?: string;
  dutyResumedByName?: string;
  dutyResumeRemarks?: string;
  extensionDays?: number;
  extensionReason?: string;
  extensionApprovedBy?: string;
  extensionApprovedAt?: Date;
  newExpectedReturnDate?: Date;
  
  // Additional Details
  addressDuringLeave: string;
  signatureFullName: string;
  passportNumber?: string;
  
  // Application Source
  appliedVia: 'engineer_portal' | 'employee_app' | 'web';
  appliedByEngineerId?: string;
  appliedByEngineerName?: string;
  engineerRemarks?: string;
  
  // Approval Status
  hrApprovalStatus: 'pending' | 'approved' | 'rejected' | 'cancelled';
  hrApprovedAt?: Date | null;
  hrManagerId?: string | null;
  hrManagerName?: string | null;
  hrRemarks?: string | null;
  
  // Cancellation
  cancelledAt?: Date | null;
  cancelledBy?: string | null;
  cancelledByName?: string | null;
  cancellationReason?: string | null;
  
  // Timestamps
  applicationDate: Date;
  createdAt: Date;
  updatedAt?: Date;
  isActive: boolean;
}

// Date overlap check result
interface DateOverlapResult {
  hasOverlap: boolean;
  conflictingLeaves: AnnualLeaveApplicationRecord[];
  message?: string;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class AnnualLeaveService {
  
  // ============================================================================
  // CHECK FOR OVERLAPPING LEAVE DATES
  // ============================================================================
  
  async checkDateOverlap(
    employeeId: string,
    startDate: Date,
    endDate: Date,
    excludeApplicationId?: string
  ): Promise<DateOverlapResult> {
    try {
      console.log('🔍 Checking annual leave date overlap for:', employeeId);
      
      const conflictingLeaves: AnnualLeaveApplicationRecord[] = [];
      const collections = ['annual_leave_applications', 'hr_leave_approvals', 'leave_applications'];
      
      for (const collectionName of collections) {
        try {
          const leaveRef = collection(db, collectionName);
          const q = query(
            leaveRef,
            where('employeeId', '==', employeeId),
            where('isActive', '==', true)
          );
          
          const snapshot = await getDocs(q);
          
          snapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            const status = data.hrApprovalStatus || data.status;
            if (status === 'cancelled' || status === 'rejected') return;
            if (excludeApplicationId && (docSnap.id === excludeApplicationId || data.applicationId === excludeApplicationId)) return;
            
            const leaveStart = this.parseTimestamp(data.startDate);
            const leaveEnd = this.parseTimestamp(data.endDate);
            const hasOverlap = startDate <= leaveEnd && endDate >= leaveStart;
            
            if (hasOverlap && !conflictingLeaves.some(l => l.applicationId === (data.applicationId || docSnap.id))) {
              conflictingLeaves.push(this.parseAnnualLeaveRecord(docSnap.id, data));
            }
          });
        } catch (e) {
          console.log(`Could not query ${collectionName}:`, e);
        }
      }
      
      if (conflictingLeaves.length > 0) {
        const conflictDetails = conflictingLeaves.map(l => 
          `${l.leaveType || 'leave'} (${this.formatDateShort(l.startDate)} - ${this.formatDateShort(l.endDate)})`
        ).join(', ');
        
        return {
          hasOverlap: true,
          conflictingLeaves,
          message: `Employee already has leave for overlapping dates: ${conflictDetails}`
        };
      }
      
      return { hasOverlap: false, conflictingLeaves: [] };
      
    } catch (error) {
      console.error('❌ Error checking date overlap:', error);
      return { hasOverlap: false, conflictingLeaves: [] };
    }
  }
  
  private formatDateShort(date: Date): string {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }
  
  // ============================================================================
  // SUBMIT ANNUAL LEAVE APPLICATION - WITH RETURN JOURNEY
  // ============================================================================
  
  async submitAnnualLeaveApplication(data: AnnualLeaveApplicationData): Promise<{ success: boolean; applicationId?: string; error?: string }> {
    try {
      console.log('📤 Submitting annual leave with return journey:', data.employeeName);
      
      // Check for date overlap
      const overlapCheck = await this.checkDateOverlap(data.employeeId, data.startDate, data.endDate);
      if (overlapCheck.hasOverlap) {
        return { success: false, error: overlapCheck.message };
      }
      
      const applicationId = `AL_ENG_${Date.now()}`;
      const now = Timestamp.now();
      
      // Generate return remarks automatically if not provided
      const autoReturnRemarks = data.returnRemarks || 
        `Employee will return to UAE on ${data.expectedReturnDate.toLocaleDateString('en-GB', { 
          weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' 
        })} via ${data.returnDepartureAirport.iataCode} → ${data.returnArrivalAirport.iataCode}`;
      
      const applicationDoc = {
        id: applicationId,
        applicationId: applicationId,
        
        // Employee Info
        employeeId: data.employeeId,
        employeeName: data.employeeName,
        employeePin: data.employeePin,
        department: data.department || '',
        designation: data.designation || '',
        
        // Leave Details
        leaveType: 'annual',
        startDate: Timestamp.fromDate(data.startDate),
        endDate: Timestamp.fromDate(data.endDate),
        totalDays: data.totalDays,
        remarks: data.remarks || '',
        
        // Project Info
        projectCode: data.projectCode || '',
        projectName: data.projectName || '',
        
        // OUTBOUND Journey (UAE → Destination)
        flightBookingType: data.flightBookingType,
        departureAirport: data.departureAirport,
        arrivalAirport: data.arrivalAirport,
        
        // RETURN Journey (Destination → UAE) - NEW
        expectedReturnDate: Timestamp.fromDate(data.expectedReturnDate),
        returnDepartureAirport: data.returnDepartureAirport,
        returnArrivalAirport: data.returnArrivalAirport,
        returnRemarks: autoReturnRemarks,
        returnFlightBookingType: data.returnFlightBookingType || data.flightBookingType,
        
        // Duty Resume Tracking - Initialize as pending
        dutyResumeStatus: 'pending',
        actualReturnDate: null,
        dutyResumedDate: null,
        dutyResumedBy: null,
        dutyResumedByName: null,
        dutyResumeRemarks: null,
        extensionDays: null,
        extensionReason: null,
        extensionApprovedBy: null,
        extensionApprovedAt: null,
        newExpectedReturnDate: null,
        
        // Additional Details
        addressDuringLeave: data.addressDuringLeave,
        signatureFullName: data.signatureFullName,
        passportNumber: data.passportNumber || '',
        
        // Engineer Portal Fields
        appliedVia: 'engineer_portal',
        appliedByEngineerId: data.appliedByEngineerId || '',
        appliedByEngineerName: data.appliedByEngineerName || '',
        engineerRemarks: data.engineerRemarks || '',
        
        // Status
        status: 'pendingHrApproval',
        currentApprovalLevel: 'hrManager',
        lineManagerApprovalStatus: 'auto_approved',
        lineManagerApprovedAt: now,
        hrApprovalStatus: 'pending',
        
        // Metadata
        applicationDate: now,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        year: new Date().getFullYear()
      };
      
      const batch = writeBatch(db);
      
      // Save to collections
      batch.set(doc(db, 'annual_leave_applications', applicationId), applicationDoc);
      batch.set(doc(db, 'leave_applications', applicationId), applicationDoc);
      batch.set(doc(db, 'hr_leave_approvals', applicationId), { ...applicationDoc, submittedToHrAt: now });
      
      // Update leave balance
      const currentYear = new Date().getFullYear();
      const balanceDocRef = doc(db, 'leave_balances', `${data.employeeId}_${currentYear}`);
      const currentBalance = await getDoc(balanceDocRef);
      
      if (currentBalance.exists()) {
        const balanceData = currentBalance.data();
        const pendingDays = balanceData.pendingDays || { annual: 0, sick: 0, local: 0, emergency: 0 };
        pendingDays.annual = (pendingDays.annual || 0) + data.totalDays;
        batch.update(balanceDocRef, { pendingDays, lastUpdated: serverTimestamp() });
      } else {
        batch.set(balanceDocRef, {
          employeeId: data.employeeId,
          year: currentYear,
          totalDays: { annual: 30, sick: 15, local: 10, emergency: 15 },
          usedDays: { annual: 0, sick: 0, local: 0, emergency: 0 },
          pendingDays: { annual: data.totalDays, sick: 0, local: 0, emergency: 0 },
          lastUpdated: serverTimestamp(),
          createdAt: serverTimestamp()
        });
      }
      
      await batch.commit();
      await this.sendHrNotification(applicationDoc);
      
      return { success: true, applicationId };
      
    } catch (error: any) {
      console.error('❌ Error submitting annual leave:', error);
      return { success: false, error: error.message || 'Failed to submit application' };
    }
  }
  
  // ============================================================================
  // DUTY RESUME TRACKING - NEW FUNCTIONS
  // ============================================================================
  
  /**
   * Get all leaves pending duty resume (employee on leave, expected to return)
   */
  async getPendingDutyResume(): Promise<AnnualLeaveApplicationRecord[]> {
    try {
      console.log('🔍 Fetching leaves pending duty resume...');
      
      const results: AnnualLeaveApplicationRecord[] = [];
      const seenIds = new Set<string>();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const annualLeaveRef = collection(db, 'annual_leave_applications');
      const q = query(
        annualLeaveRef,
        where('hrApprovalStatus', '==', 'approved'),
        where('dutyResumeStatus', '==', 'pending'),
        where('isActive', '==', true)
      );
      
      try {
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          const appId = data.applicationId || docSnap.id;
          if (!seenIds.has(appId)) {
            seenIds.add(appId);
            results.push(this.parseAnnualLeaveRecord(docSnap.id, data));
          }
        });
      } catch (e) {
        // Fallback without dutyResumeStatus filter
        const qFallback = query(
          annualLeaveRef,
          where('hrApprovalStatus', '==', 'approved'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(qFallback);
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          const status = data.dutyResumeStatus || 'pending';
          if (status === 'pending') {
            const appId = data.applicationId || docSnap.id;
            if (!seenIds.has(appId)) {
              seenIds.add(appId);
              results.push(this.parseAnnualLeaveRecord(docSnap.id, data));
            }
          }
        });
      }
      
      // Sort by expected return date
      results.sort((a, b) => {
        const aDate = a.expectedReturnDate?.getTime() || 0;
        const bDate = b.expectedReturnDate?.getTime() || 0;
        return aDate - bDate;
      });
      
      console.log(`✅ Found ${results.length} leaves pending duty resume`);
      return results;
      
    } catch (error) {
      console.error('❌ Error fetching pending duty resume:', error);
      return [];
    }
  }
  
  /**
   * Get leaves where employee should have returned (overdue)
   */
  async getOverdueReturns(): Promise<AnnualLeaveApplicationRecord[]> {
    try {
      const pendingResume = await this.getPendingDutyResume();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return pendingResume.filter(leave => {
        const expectedReturn = leave.newExpectedReturnDate || leave.expectedReturnDate;
        return expectedReturn && expectedReturn < today;
      });
      
    } catch (error) {
      console.error('❌ Error fetching overdue returns:', error);
      return [];
    }
  }
  
  /**
   * Get leaves with return expected today
   */
  async getReturnsExpectedToday(): Promise<AnnualLeaveApplicationRecord[]> {
    try {
      const pendingResume = await this.getPendingDutyResume();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      return pendingResume.filter(leave => {
        const expectedReturn = leave.newExpectedReturnDate || leave.expectedReturnDate;
        return expectedReturn && expectedReturn >= today && expectedReturn < tomorrow;
      });
      
    } catch (error) {
      console.error('❌ Error fetching returns expected today:', error);
      return [];
    }
  }
  
  /**
   * Mark employee as resumed duty
   */
  async markDutyResumed(
    applicationId: string,
    resumedBy: string,
    resumedByName: string,
    actualReturnDate: Date,
    dutyResumedDate: Date,
    remarks?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('✅ Marking duty resumed for:', applicationId);
      
      const batch = writeBatch(db);
      const now = Timestamp.now();
      
      const updateData = {
        dutyResumeStatus: 'resumed',
        actualReturnDate: Timestamp.fromDate(actualReturnDate),
        dutyResumedDate: Timestamp.fromDate(dutyResumedDate),
        dutyResumedBy: resumedBy,
        dutyResumedByName: resumedByName,
        dutyResumeRemarks: remarks || `Employee resumed duty on ${dutyResumedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`,
        updatedAt: now
      };
      
      // Update all collections
      const collections = ['annual_leave_applications', 'leave_applications', 'hr_leave_approvals'];
      for (const collectionName of collections) {
        const docRef = doc(db, collectionName, applicationId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          batch.update(docRef, updateData);
        }
      }
      
      await batch.commit();
      
      // Send notification
      await this.sendDutyResumeNotification(applicationId, resumedByName, dutyResumedDate);
      
      console.log('✅ Duty resume marked successfully');
      return { success: true };
      
    } catch (error: any) {
      console.error('❌ Error marking duty resumed:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Mark employee as not returned / absent
   */
  async markNotReturned(
    applicationId: string,
    markedBy: string,
    markedByName: string,
    remarks?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('⚠️ Marking as not returned:', applicationId);
      
      const batch = writeBatch(db);
      const now = Timestamp.now();
      
      const updateData = {
        dutyResumeStatus: 'not_returned',
        dutyResumeRemarks: remarks || `Employee did not return as expected. Marked by ${markedByName}`,
        updatedAt: now
      };
      
      const collections = ['annual_leave_applications', 'leave_applications', 'hr_leave_approvals'];
      for (const collectionName of collections) {
        const docRef = doc(db, collectionName, applicationId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          batch.update(docRef, updateData);
        }
      }
      
      await batch.commit();
      
      console.log('✅ Marked as not returned');
      return { success: true };
      
    } catch (error: any) {
      console.error('❌ Error marking not returned:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Extend leave / Update expected return date
   */
  async extendLeave(
    applicationId: string,
    newExpectedReturnDate: Date,
    extensionDays: number,
    extensionReason: string,
    approvedBy: string,
    approvedByName: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('📅 Extending leave:', applicationId);
      
      const batch = writeBatch(db);
      const now = Timestamp.now();
      
      const updateData = {
        dutyResumeStatus: 'extended',
        newExpectedReturnDate: Timestamp.fromDate(newExpectedReturnDate),
        extensionDays,
        extensionReason,
        extensionApprovedBy: approvedBy,
        extensionApprovedByName: approvedByName,
        extensionApprovedAt: now,
        returnRemarks: `Leave extended by ${extensionDays} days. New return date: ${newExpectedReturnDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}. Reason: ${extensionReason}`,
        updatedAt: now
      };
      
      const collections = ['annual_leave_applications', 'leave_applications', 'hr_leave_approvals'];
      for (const collectionName of collections) {
        const docRef = doc(db, collectionName, applicationId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          batch.update(docRef, updateData);
        }
      }
      
      await batch.commit();
      
      console.log('✅ Leave extended successfully');
      return { success: true };
      
    } catch (error: any) {
      console.error('❌ Error extending leave:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Search leave applications by employee PIN for duty resume
   */
  async searchForDutyResume(searchTerm: string): Promise<AnnualLeaveApplicationRecord[]> {
    try {
      console.log('🔍 Searching for duty resume:', searchTerm);
      
      const results: AnnualLeaveApplicationRecord[] = [];
      const seenIds = new Set<string>();
      const cleanSearch = searchTerm.toUpperCase().replace('EMP', '').trim();
      
      const annualLeaveRef = collection(db, 'annual_leave_applications');
      const q = query(
        annualLeaveRef,
        where('hrApprovalStatus', '==', 'approved'),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const pin = (data.employeePin || '').toUpperCase().replace('EMP', '');
        const name = (data.employeeName || '').toUpperCase();
        const appId = (data.applicationId || docSnap.id).toUpperCase();
        
        // Match by PIN, name, or application ID
        if (pin.includes(cleanSearch) || name.includes(cleanSearch.toUpperCase()) || appId.includes(cleanSearch)) {
          const id = data.applicationId || docSnap.id;
          if (!seenIds.has(id)) {
            seenIds.add(id);
            results.push(this.parseAnnualLeaveRecord(docSnap.id, data));
          }
        }
      });
      
      // Sort: pending first, then by expected return date
      results.sort((a, b) => {
        if (a.dutyResumeStatus === 'pending' && b.dutyResumeStatus !== 'pending') return -1;
        if (a.dutyResumeStatus !== 'pending' && b.dutyResumeStatus === 'pending') return 1;
        return (b.expectedReturnDate?.getTime() || 0) - (a.expectedReturnDate?.getTime() || 0);
      });
      
      console.log(`✅ Found ${results.length} matching records`);
      return results;
      
    } catch (error) {
      console.error('❌ Error searching for duty resume:', error);
      return [];
    }
  }
  
  // ============================================================================
  // GET ANNUAL LEAVE APPLICATIONS
  // ============================================================================
  
  async getEngineerAnnualLeaves(engineerId: string): Promise<AnnualLeaveApplicationRecord[]> {
    try {
      const applications: AnnualLeaveApplicationRecord[] = [];
      const seenIds = new Set<string>();
      
      const annualLeaveRef = collection(db, 'annual_leave_applications');
      
      try {
        const q = query(
          annualLeaveRef,
          where('appliedByEngineerId', '==', engineerId),
          orderBy('applicationDate', 'desc')
        );
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(docSnap => {
          const appId = docSnap.data().applicationId || docSnap.id;
          if (!seenIds.has(appId)) {
            seenIds.add(appId);
            applications.push(this.parseAnnualLeaveRecord(docSnap.id, docSnap.data()));
          }
        });
      } catch (e) {
        const allSnapshot = await getDocs(annualLeaveRef);
        allSnapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          if (data.appliedByEngineerId === engineerId) {
            const appId = data.applicationId || docSnap.id;
            if (!seenIds.has(appId)) {
              seenIds.add(appId);
              applications.push(this.parseAnnualLeaveRecord(docSnap.id, data));
            }
          }
        });
      }
      
      applications.sort((a, b) => b.applicationDate.getTime() - a.applicationDate.getTime());
      return applications;
      
    } catch (error) {
      console.error('❌ Error fetching annual leaves:', error);
      return [];
    }
  }
  
  async getEmployeeAnnualLeaves(employeeId: string, maxResults: number = 10): Promise<AnnualLeaveApplicationRecord[]> {
    try {
      const applications: AnnualLeaveApplicationRecord[] = [];
      const seenIds = new Set<string>();
      
      const annualLeaveRef = collection(db, 'annual_leave_applications');
      const q = query(annualLeaveRef, where('employeeId', '==', employeeId), limit(maxResults));
      
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(docSnap => {
        const appId = docSnap.data().applicationId || docSnap.id;
        if (!seenIds.has(appId)) {
          seenIds.add(appId);
          applications.push(this.parseAnnualLeaveRecord(docSnap.id, docSnap.data()));
        }
      });
      
      applications.sort((a, b) => b.applicationDate.getTime() - a.applicationDate.getTime());
      return applications.slice(0, maxResults);
      
    } catch (error) {
      console.error('❌ Error fetching employee annual leaves:', error);
      return [];
    }
  }
  
  // ============================================================================
  // CANCEL ANNUAL LEAVE
  // ============================================================================
  
  async cancelAnnualLeave(
    applicationId: string,
    cancelledBy: string,
    cancelledByName: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();
      
      let applicationData: any = null;
      let employeeId = '';
      let totalDays = 0;
      
      const annualLeaveRef = doc(db, 'annual_leave_applications', applicationId);
      const annualLeaveSnap = await getDoc(annualLeaveRef);
      
      if (annualLeaveSnap.exists()) {
        applicationData = annualLeaveSnap.data();
        employeeId = applicationData.employeeId;
        totalDays = applicationData.totalDays || 0;
        
        if (applicationData.hrApprovalStatus !== 'pending') {
          return { success: false, error: `Cannot cancel - already ${applicationData.hrApprovalStatus}` };
        }
        
        const cancelData = {
          hrApprovalStatus: 'cancelled',
          status: 'cancelled',
          cancelledAt: now,
          cancelledBy,
          cancelledByName,
          cancellationReason: reason || 'Cancelled',
          isActive: false,
          updatedAt: now
        };
        
        batch.update(annualLeaveRef, cancelData);
        
        // Update other collections
        const hrRef = doc(db, 'hr_leave_approvals', applicationId);
        const leaveRef = doc(db, 'leave_applications', applicationId);
        if ((await getDoc(hrRef)).exists()) batch.update(hrRef, cancelData);
        if ((await getDoc(leaveRef)).exists()) batch.update(leaveRef, cancelData);
      }
      
      if (!applicationData) {
        return { success: false, error: 'Application not found' };
      }
      
      // Restore balance
      const currentYear = new Date().getFullYear();
      const balanceDocRef = doc(db, 'leave_balances', `${employeeId}_${currentYear}`);
      const balanceSnap = await getDoc(balanceDocRef);
      
      if (balanceSnap.exists()) {
        const balanceData = balanceSnap.data();
        const pendingDays = balanceData.pendingDays || { annual: 0 };
        pendingDays.annual = Math.max(0, (pendingDays.annual || 0) - totalDays);
        batch.update(balanceDocRef, { pendingDays, lastUpdated: now });
      }
      
      await batch.commit();
      await this.sendCancellationNotification(applicationData, cancelledByName, reason);
      
      return { success: true };
      
    } catch (error: any) {
      console.error('❌ Error cancelling:', error);
      return { success: false, error: error.message };
    }
  }
  
  // ============================================================================
  // HELPER METHODS
  // ============================================================================
  
  private parseAnnualLeaveRecord(id: string, data: any): AnnualLeaveApplicationRecord {
    return {
      id,
      applicationId: data.applicationId || id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      employeePin: data.employeePin || '',
      department: data.department,
      designation: data.designation,
      leaveType: 'annual',
      startDate: this.parseTimestamp(data.startDate),
      endDate: this.parseTimestamp(data.endDate),
      totalDays: data.totalDays || 0,
      remarks: data.remarks,
      projectCode: data.projectCode,
      projectName: data.projectName,
      
      // Outbound journey
      flightBookingType: data.flightBookingType || 'byCompany',
      departureAirport: data.departureAirport || { iataCode: '', name: '', city: '', country: '' },
      arrivalAirport: data.arrivalAirport || { iataCode: '', name: '', city: '', country: '' },
      
      // Return journey
      expectedReturnDate: this.parseTimestamp(data.expectedReturnDate || data.endDate),
      returnDepartureAirport: data.returnDepartureAirport || data.arrivalAirport || { iataCode: '', name: '', city: '', country: '' },
      returnArrivalAirport: data.returnArrivalAirport || data.departureAirport || { iataCode: '', name: '', city: '', country: '' },
      returnRemarks: data.returnRemarks,
      returnFlightBookingType: data.returnFlightBookingType || data.flightBookingType,
      
      // Duty resume tracking
      dutyResumeStatus: data.dutyResumeStatus || 'pending',
      actualReturnDate: data.actualReturnDate ? this.parseTimestamp(data.actualReturnDate) : undefined,
      dutyResumedDate: data.dutyResumedDate ? this.parseTimestamp(data.dutyResumedDate) : undefined,
      dutyResumedBy: data.dutyResumedBy,
      dutyResumedByName: data.dutyResumedByName,
      dutyResumeRemarks: data.dutyResumeRemarks,
      extensionDays: data.extensionDays,
      extensionReason: data.extensionReason,
      extensionApprovedBy: data.extensionApprovedBy,
      extensionApprovedAt: data.extensionApprovedAt ? this.parseTimestamp(data.extensionApprovedAt) : undefined,
      newExpectedReturnDate: data.newExpectedReturnDate ? this.parseTimestamp(data.newExpectedReturnDate) : undefined,
      
      // Additional details
      addressDuringLeave: data.addressDuringLeave || '',
      signatureFullName: data.signatureFullName || '',
      passportNumber: data.passportNumber || '',
      
      appliedVia: data.appliedVia || 'employee_app',
      appliedByEngineerId: data.appliedByEngineerId,
      appliedByEngineerName: data.appliedByEngineerName,
      engineerRemarks: data.engineerRemarks,
      
      hrApprovalStatus: data.hrApprovalStatus || data.status || 'pending',
      hrApprovedAt: data.hrApprovedAt ? this.parseTimestamp(data.hrApprovedAt) : null,
      hrManagerId: data.hrManagerId || null,
      hrManagerName: data.hrManagerName || null,
      hrRemarks: data.hrRemarks || null,
      
      cancelledAt: data.cancelledAt ? this.parseTimestamp(data.cancelledAt) : null,
      cancelledBy: data.cancelledBy || null,
      cancelledByName: data.cancelledByName || null,
      cancellationReason: data.cancellationReason || null,
      
      applicationDate: this.parseTimestamp(data.applicationDate || data.createdAt),
      createdAt: this.parseTimestamp(data.createdAt || data.applicationDate),
      updatedAt: data.updatedAt ? this.parseTimestamp(data.updatedAt) : undefined,
      isActive: data.isActive !== false
    };
  }
  
  private parseTimestamp(value: any): Date {
    if (!value) return new Date();
    if (value.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    if (typeof value === 'number') return new Date(value);
    return new Date();
  }
  
  calculateTotalDays(startDate: Date, endDate: Date): number {
    if (startDate > endDate) return 0;
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }
  
  private async sendHrNotification(applicationDoc: any): Promise<void> {
    try {
      const notification = {
        type: 'annual_leave_application',
        title: '✈️ Annual Leave Application',
        body: `${applicationDoc.employeeName} - Annual Leave (${applicationDoc.totalDays} days)`,
        message: `Route: ${applicationDoc.departureAirport?.iataCode} → ${applicationDoc.arrivalAirport?.iataCode}. Expected Return: ${this.formatDateShort(this.parseTimestamp(applicationDoc.expectedReturnDate))}`,
        applicationId: applicationDoc.id,
        employeeId: applicationDoc.employeeId,
        employeeName: applicationDoc.employeeName,
        targetRole: 'hr_manager',
        isRead: false,
        priority: 'high',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'hr_notifications'), notification);
      await addDoc(collection(db, 'hr_live_notifications'), notification);
    } catch (error) {
      console.error('❌ Error sending notification:', error);
    }
  }
  
  private async sendCancellationNotification(applicationData: any, cancelledByName: string, reason?: string): Promise<void> {
    try {
      await addDoc(collection(db, 'hr_notifications'), {
        type: 'annual_leave_cancelled',
        title: '🚫 Annual Leave Cancelled',
        body: `${applicationData.employeeName}'s leave cancelled`,
        message: reason || 'No reason provided',
        applicationId: applicationData.applicationId,
        targetRole: 'hr_manager',
        isRead: false,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Error sending notification:', error);
    }
  }
  
  private async sendDutyResumeNotification(applicationId: string, resumedByName: string, dutyDate: Date): Promise<void> {
    try {
      await addDoc(collection(db, 'hr_notifications'), {
        type: 'duty_resumed',
        title: '✅ Employee Resumed Duty',
        body: `Duty resumed on ${this.formatDateShort(dutyDate)}`,
        applicationId,
        markedBy: resumedByName,
        targetRole: 'hr_manager',
        isRead: false,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Error sending notification:', error);
    }
  }
}

export const annualLeaveService = new AnnualLeaveService();
export default annualLeaveService;